import React, { Component } from "react";
import styled from "styled-components";
import moment from "moment";

import { Button, NOTICE_TYPES } from "@appcues/component-library";
import BaseModal from "components/ModalContainer/BaseModal";
import ExportInputFields from "components/ModalContainer/ExportModal/ExportInputFields";
import ExportModalStatusMessage from "components/ModalContainer/ExportModal/ExportModalStatusMessage";
import ExportSuccessMessage from "components/ModalContainer/ExportModal/ExportSuccessMessage";

import { exportModalMessages } from "constants/modalMessages";
import { reportError } from "helpers/error-reporting";
import { REQUESTED_CSV_EXPORT } from "constants/events";
import { requestCsvExport } from "helpers/csvExports";

class ExportModal extends Component {
  static defaultProps = {
    includeFormResponsesOption: true
  };

  constructor(props) {
    super(props);

    // Need to bind this because it's async and we can't use class property
    // syntax and arrow function to bind an async function.
    this.callExtractionApi = this.callExtractionApi.bind(this);
  }

  state = {
    dateRange: this.props.createdAt,
    email: this.props.email,
    formResponsesOnly: false,
    pending: false,
    statusText: null,
    status: NOTICE_TYPES.default,
    startDate:
      moment()
        .subtract(7, "days")
        .valueOf() > this.props.createdAt
        ? moment().subtract(7, "days")
        : moment(this.props.createdAt),
    endDate: moment(),
    exportMode: "basic"
  };

  resetModal = () => {
    this.setState({
      status: NOTICE_TYPES.default,
      statusText: null
    });
  };

  handleEmailChange = event => {
    this.setState({ email: event.target.value });
  };

  handleEnterPress = event => {
    if (event.key === "Enter") {
      this.handleSubmit();
    }
  };

  handleExportModeChange = mode => {
    this.setState({ exportMode: mode });
  };

  handleFormResponsesChange = evt => {
    const formResponsesOnly = evt.target.checked;
    this.setState({ formResponsesOnly });
  };

  handleDateRangeSelect = selectedOption => {
    this.setState({ dateRange: selectedOption.value });
  };

  handleCustomDateRangeSelect = (type, selectedOption) => {
    this.setState({ [type]: selectedOption });
  };

  handleStartDateSelect = option => {
    const { endDate } = this.state;

    if (option.isAfter(endDate)) {
      const newEndDate = moment(option.valueOf()).add(1, "days");
      const validNewEndDate = newEndDate.isAfter(moment())
        ? moment()
        : newEndDate;

      this.handleCustomDateRangeSelect("endDate", validNewEndDate);
    }

    this.handleCustomDateRangeSelect("startDate", option);
  };

  handleEndDateSelect = option => {
    const { startDate } = this.state;
    const { createdAt } = this.props;

    if (option.isBefore(startDate)) {
      const newStartDate = moment(option.valueOf()).subtract(1, "days");
      const validNewStartDate = newStartDate.isBefore(moment(createdAt))
        ? moment(createdAt)
        : newStartDate;

      this.handleCustomDateRangeSelect("startDate", validNewStartDate);
    }

    this.handleCustomDateRangeSelect("endDate", option);
  };

  handleSubmit = () => {
    const {
      accountId,
      actions: { trackEvent },
      exportType,
      exportId
    } = this.props;
    const { email, exportMode } = this.state;

    if (this.validInput(email)) {
      trackEvent(REQUESTED_CSV_EXPORT, {
        accountId: accountId,
        to_email: email,
        [exportType]: exportId,
        type: exportMode,
        contentType: "flow"
      });

      this.setState(
        {
          pending: true,
          status: NOTICE_TYPES.primary,
          statusText: "Asking for your csv!"
        },
        this.callExtractionApi
      );
    } else {
      this.setState({
        statusText: "Make sure you type in an email!",
        status: NOTICE_TYPES.warning
      });
    }
  };

  validInput = input => {
    return input !== "";
  };

  getUnixStartTimestamp = () => {
    const { createdAt } = this.props;
    const { dateRange } = this.state;

    const getUnixTime = numberOfDays => {
      return moment()
        .subtract(numberOfDays, "days")
        .valueOf();
    };

    const unixDateRangeStart =
      dateRange === createdAt ? createdAt : getUnixTime(dateRange);

    const unixStartDate =
      unixDateRangeStart < createdAt ? createdAt : unixDateRangeStart;

    return unixStartDate;
  };

  /**
   * @todo the date normalization here could probably live in the
   * csvExports helper
   */
  callExtractionApi() {
    const { accountId, createdAt, exportId, exportType, uuid } = this.props;
    const {
      exportMode,
      dateRange,
      email,
      endDate,
      startDate,
      formResponsesOnly
    } = this.state;
    const csvType = exportType === "checklist_id" ? "checklist" : "flow";

    const publishedAfterStartOfToday =
      startDate &&
      startDate.startOf("day") &&
      startDate.startOf("day").valueOf() < createdAt;

    let normalizedStartDate;
    if (dateRange === "custom" && publishedAfterStartOfToday) {
      normalizedStartDate = createdAt;
    } else if (dateRange === "custom" && !publishedAfterStartOfToday) {
      normalizedStartDate = startDate.startOf("day").valueOf();
    } else {
      normalizedStartDate = this.getUnixStartTimestamp();
    }

    const requestOpts = {
      accountApiKey: uuid,
      accountId,
      startDate: normalizedStartDate,
      email,
      filter: {
        [exportType]: exportId
      },
      basic: exportMode === "basic",
      formResponsesOnly
    };

    const endOfRangeBeforeNow =
      endDate &&
      endDate.endOf("day") &&
      endDate.endOf("day").valueOf() < new Date().getTime();

    if (dateRange === "custom" && endOfRangeBeforeNow) {
      requestOpts.endDate = endDate.endOf("day").valueOf();
    }

    requestCsvExport(csvType, requestOpts).then(result => {
      if (result.error) {
        reportError(result.error);

        this.setState({
          pending: false,
          statusText:
            "There was a problem submitting your request, please try again later.",
          status: NOTICE_TYPES.warning
        });
      } else {
        this.setState({
          pending: false,
          statusText: "Awesome! Your CSV is on the way!",
          status: NOTICE_TYPES.success
        });
      }
    });
  }

  render() {
    const {
      actions: { hideModal },
      createdAt,
      includeFormResponsesOption
    } = this.props;
    const {
      exportMode,
      dateRange,
      email,
      endDate,
      formResponsesOnly,
      pending,
      startDate,
      status,
      statusText
    } = this.state;

    const modalTitle =
      status === NOTICE_TYPES.success
        ? "Analytics on the way!"
        : "Export a CSV";

    return (
      <ExportModalWrapper>
        <BaseModal
          modalClass="export-modal"
          shouldHideButtonStyle
          hideOKButton
          title={modalTitle}
          {...this.props}
        >
          {status === NOTICE_TYPES.success ? (
            <ExportSuccessMessage
              email={email}
              resetModal={this.resetModal}
              hideModal={hideModal}
            />
          ) : (
            <div>
              <ExportInputFields
                exportMode={exportMode}
                createdAt={createdAt}
                dateRange={dateRange}
                email={email}
                endDate={endDate}
                formResponsesOnly={formResponsesOnly}
                handleEmailChange={this.handleEmailChange}
                handleEnterPress={this.handleEnterPress}
                handleExportModeChange={this.handleExportModeChange}
                handleFormResponsesChange={this.handleFormResponsesChange}
                handleDateRangeSelect={this.handleDateRangeSelect}
                handleStartDateSelect={this.handleStartDateSelect}
                handleEndDateSelect={this.handleEndDateSelect}
                startDate={startDate}
                includeFormResponsesOption={includeFormResponsesOption}
              />
              <ExportModalStatusMessage status={status} statusText={statusText}>
                <span>{statusText}</span>
              </ExportModalStatusMessage>
              <ButtonWrapper>
                <Button onClick={hideModal}>Cancel</Button>
                <Button
                  disabled={pending}
                  onClick={this.handleSubmit}
                  className="button-primary"
                >
                  {exportModalMessages.buttonText}
                </Button>
              </ButtonWrapper>
            </div>
          )}
        </BaseModal>
      </ExportModalWrapper>
    );
  }
}

const ExportModalWrapper = styled.div`
  .modal-body {
    padding: 1.125rem 0 !important;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;

  .button {
    display: inline-block;
  }

  .button-primary {
    margin-left: 16px;
  }
`;

export default ExportModal;
